import React, { useEffect, useState } from "react";
import {
  Container,
  TextField,
  Button,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper, FormControl, InputLabel, Select, InputAdornment, MenuItem,
  IconButton,
  Modal,
  Backdrop,
  Fade,
  AppBar,
  Toolbar,
  Typography,
  Box
} from "@mui/material";
import { Edit, Delete, Visibility, Print, NavigateBefore, NavigateNext, CloudDownload } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import IdCard from '@mui/icons-material/Badge';
import jsPDF from "jspdf";
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import CloseIcon from '@mui/icons-material/Close';
import { useParams } from "react-router-dom";
import base64 from "base64-js";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CircularProgress from "@mui/material/CircularProgress";

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

// Enhanced PDF Image Manager with improved sequencing and error handling
class ImprovedPDFImageManager {
  constructor() {
    this.imageCache = new Map();
    this.maxCacheSize = 50;
  }

  // ✅ NEW: Fetch school details from same API used in admission form
  async fetchSchoolDetails() {
    try {
      const token = sessionStorage.getItem("token");
      if (!token) throw new Error("No auth token found");

      const response = await fetch('https://wsms.aidigitalsec.com/api/FeeReport/GetSchoolName', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token,
        },
        body: JSON.stringify({}),
      });

      if (!response.ok) throw new Error(`API error: ${response.status}`);

      const apiData = await response.json();
      const school = apiData[0];

      // Process logo from API response
      let logoDataUrl = null;
      const logoImg = school?.logoImg;
      if (logoImg && typeof logoImg === 'string' && logoImg.trim() !== '') {
        try {
          let cleanedBase64 = logoImg
            .replace(/^data:image\/[a-zA-Z]+;base64,/, '')
            .replace(/\s/g, '')
            .replace(/\n/g, '');
          const paddingLength = (4 - (cleanedBase64.length % 4)) % 4;
          cleanedBase64 += '='.repeat(paddingLength);
          if (/^[A-Za-z0-9+/=]+$/.test(cleanedBase64)) {
            logoDataUrl = `data:image/png;base64,${cleanedBase64}`;
          }
        } catch (e) {
          console.warn("⚠️ Logo processing failed:", e.message);
        }
      }

      return {
        schoolName: school?.schoolName?.replace(/['"]+/g, '') || 'N/A',
        address: school?.address?.replace(/['"]+/g, '') || '',
        phoneNo: school?.phoneNo?.replace(/['"]+/g, '') || '',
        logoDataUrl,
      };
    } catch (error) {
      console.error("❌ Failed to fetch school details:", error.message);
      // Fallback to sessionStorage if API fails
      return {
        schoolName: sessionStorage.getItem("organizationName")?.replace(/['"]+/g, '') || 'N/A',
        address: sessionStorage.getItem("address")?.replace(/['"]+/g, '') || '',
        phoneNo: sessionStorage.getItem("phoneNo")?.replace(/['"]+/g, '') || '',
        logoDataUrl: null,
      };
    }
  }

  // ✅ UPDATED: Main PDF generation — fetches school details from API first
  async generateIdCardPDF(studentData) {
    try {
      console.log("🔄 Starting ID Card PDF generation...");
      this.showLoadingIndicator();

      // Step 1: Fetch school details from API
      console.log("🏫 Step 1: Fetching school details from API...");
      const schoolDetails = await this.fetchSchoolDetails();
      console.log("✅ School details fetched:", schoolDetails.schoolName);

      // Step 2: Load and validate all images BEFORE building PDF
      console.log("📸 Step 2: Loading all images...");
      const [studentImageResult] = await Promise.allSettled([
        this.handleStudentImage(studentData)
      ]);

      // Process student image result
      let validStudentImageResult = null;
      if (studentImageResult.status === 'fulfilled' && studentImageResult.value) {
        validStudentImageResult = studentImageResult.value;
        console.log("✅ Student image loaded successfully");
      } else {
        console.warn("⚠️ Student image loading failed, using default avatar");
        validStudentImageResult = this.createDefaultAvatar();
      }

      // Process logo from school details
      let validLogoResult = null;
      if (schoolDetails.logoDataUrl) {
        validLogoResult = { type: 'base64', data: schoolDetails.logoDataUrl };
        console.log("✅ Logo prepared from API");
      } else {
        console.warn("⚠️ No logo from API, trying URL-based logo...");
        const logoUrlResult = await this.handleLogoImage();
        if (logoUrlResult) validLogoResult = logoUrlResult;
      }

      // Step 3: Validate images before proceeding
      console.log("🔍 Step 3: Validating images...");
      const [logoValidated, studentImageValidated] = await Promise.all([
        validLogoResult ? this.validateImageData(validLogoResult) : Promise.resolve(null),
        this.validateImageData(validStudentImageResult)
      ]);

      if (!studentImageValidated) {
        console.warn("⚠️ Student image is not valid, cannot generate ID card.");
        this.hideLoadingIndicator();
        return;
      }

      // Step 4: Build PDF
      const doc = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: [90, 115],
      });

      // Step 5: Build PDF structure with school details from API
      console.log("🏗️ Step 5: Building PDF structure...");
      await this.buildPDFStructure(doc, studentData, schoolDetails, logoValidated);

      // Step 6: Add validated student image
      console.log("🖼️ Step 6: Adding student image...");
      await this.addValidatedImageToPDF(doc, studentImageValidated, 32.5, 27, 25, 25);

      // Step 7: Add student content with school details from API
      console.log("📝 Step 7: Adding student content...");
      this.addStudentContent(doc, studentData, schoolDetails);

      // Step 8: Generate and open PDF
      console.log("📄 Step 8: Generating final PDF...");
      this.generateAndOpenPDF(doc);

      console.log("✅ ID Card PDF generated successfully");
      this.hideLoadingIndicator();

    } catch (error) {
      console.error("❌ PDF generation failed:", error);
      this.hideLoadingIndicator();
      this.showErrorMessage(`Failed to generate ID card: ${error.message}`);
    }
  }

  // New method to validate image data before using in PDF
  async validateImageData(imageResult) {
    if (!imageResult || !imageResult.data) {
      return null;
    }

    return new Promise((resolve) => {
      const testImg = new Image();
      const timeout = setTimeout(() => {
        console.warn("⚠️ Image validation timeout");
        resolve(null);
      }, 5000);

      testImg.onload = () => {
        clearTimeout(timeout);
        console.log("✅ Image validation successful");
        resolve(imageResult);
      };

      testImg.onerror = (error) => {
        clearTimeout(timeout);
        console.warn("⚠️ Image validation failed:", error);
        resolve(null);
      };

      testImg.src = imageResult.data;
    });
  }

  // ✅ UPDATED: Build PDF structure using school details from API
  async buildPDFStructure(doc, studentData, schoolDetails, validatedLogoResult) {
    const pageWidth = 90, pageHeight = 117;
    const logoX = 3, logoY = 2, logoWidth = 14, logoHeight = 14;
    const textXStart = logoX + logoWidth + 3;
    const textMaxWidth = pageWidth - textXStart - 3;
    const headerHeight = 20;
    const verticalTop = 8;

    // White background
    doc.setFillColor(255, 255, 255);
    doc.rect(0, 0, pageWidth, pageHeight, "F");

    // Dark blue header
    doc.setFillColor(0, 51, 102);
    doc.rect(0, 0, pageWidth, headerHeight, "F");

    // Add validated logo if available
    if (validatedLogoResult) {
      try {
        await this.addValidatedImageToPDF(doc, validatedLogoResult, logoX, logoY, logoWidth, logoHeight);
        console.log("✅ Logo added to PDF");
      } catch (error) {
        console.warn("⚠️ Failed to add logo to PDF:", error.message);
      }
    } else {
      console.log("ℹ️ No valid logo available, continuing without logo");
    }

    // ✅ Use school name from API
    const schoolName = schoolDetails.schoolName;
    const sessionText = (studentData.session || "Session 2024-2025") + " (Recognized)";

    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(255, 255, 255);

    const nameLines = doc.splitTextToSize(schoolName, textMaxWidth);
    const maxLineWidth = Math.max(...nameLines.map(line => doc.getTextWidth(line)));

    let currentY = verticalTop;
    nameLines.forEach(line => {
      doc.text(line, textXStart, currentY);
      currentY += 4.5;
    });

    doc.setFontSize(9);
    const sessionX = textXStart + maxLineWidth / 2;
    doc.text(sessionText, sessionX, currentY + 1, { align: 'center' });

    // Body box
    doc.rect(2, 23, pageWidth - 4, pageHeight - 33);
  }

  // Enhanced image addition with better error handling
  async addValidatedImageToPDF(doc, validatedImageResult, x, y, width, height) {
    if (!validatedImageResult || !validatedImageResult.data) {
      console.warn("⚠️ No validated image data, adding placeholder");
      this.addPlaceholderToPDF(doc, x, y, width, height);
      return;
    }

    return new Promise((resolve) => {
      try {
        console.log("🔄 Adding validated image to PDF...");

        const formats = ['JPEG', 'PNG', 'GIF', 'WEBP'];
        let success = false;
        let lastError = null;

        for (const format of formats) {
          try {
            doc.addImage(validatedImageResult.data, format, x, y, width, height);
            console.log(`✅ Successfully added image as ${format}`);
            success = true;
            break;
          } catch (formatError) {
            console.warn(`⚠️ Failed to add image as ${format}:`, formatError.message);
            lastError = formatError;
          }
        }

        if (!success) {
          console.warn("⚠️ All image formats failed, adding placeholder");
          console.warn("Last error:", lastError?.message);
          this.addPlaceholderToPDF(doc, x, y, width, height);
        }

        resolve();
      } catch (error) {
        console.error("❌ Error in addValidatedImageToPDF:", error);
        this.addPlaceholderToPDF(doc, x, y, width, height);
        resolve();
      }
    });
  }

  // Logo handling with better debugging (fallback if API logo is absent)
  async handleLogoImage() {
    console.log("=== LOGO DEBUGGING START ===");

    const logoUrl = sessionStorage.getItem("logoImg");
    console.log("Raw logo URL from session:", logoUrl);

    if (!logoUrl || logoUrl.trim() === '') {
      console.log("❌ No logo URL found in sessionStorage");
      return null;
    }

    const cleanedUrl = logoUrl.replace(/['"]+/g, '').trim();
    console.log("Cleaned logo URL:", cleanedUrl);

    if (cleanedUrl.startsWith('data:image/')) {
      console.log("✅ Using direct base64 logo");
      return { type: 'base64', data: cleanedUrl };
    }

    if (this.isBase64String(cleanedUrl)) {
      console.log("✅ Formatting raw base64 logo");
      const formattedLogo = this.formatBase64ForPDF(cleanedUrl);
      return { type: 'base64', data: formattedLogo };
    }

    const cacheKey = `logo_${cleanedUrl}`;
    const cachedLogo = this.getCachedImage(cacheKey);
    if (cachedLogo) {
      console.log("✅ Using cached logo");
      return { type: 'base64', data: cachedLogo };
    }

    console.log("🔄 Attempting to load logo from URL...");
    const logoSources = this.getLogoSources(cleanedUrl);

    for (let i = 0; i < logoSources.length; i++) {
      const source = logoSources[i];
      console.log(`Trying source ${i + 1}/${logoSources.length}: ${source.name}`);
      try {
        const logoData = await this.loadImageFromSource(source);
        if (logoData) {
          console.log(`✅ Successfully loaded logo from ${source.name}`);
          this.cacheImage(cacheKey, logoData);
          return { type: 'base64', data: logoData };
        }
      } catch (error) {
        console.warn(`❌ Failed to load logo from ${source.name}:`, error.message);
      }
    }

    console.log("❌ All logo sources failed");
    console.log("=== LOGO DEBUGGING END ===");
    return null;
  }

  // Student image handling
  async handleStudentImage(studentData) {
  console.log("=== STUDENT IMAGE DEBUGGING START ===");
  const imagePath = studentData.studentImagePath || studentData.studentProfileLogo || studentData.profileImage;
  console.log("Student image path:", imagePath);

  if (!imagePath || imagePath.trim() === '') {
    console.log("❌ No student image path provided, using default avatar");
    return this.createDefaultAvatar();
  }

  const cleanedPath = imagePath.replace(/['"]+/g, '').trim();
  console.log("Cleaned student image path:", cleanedPath);

  // ✅ Already base64
  if (cleanedPath.startsWith('data:image/')) {
    console.log("✅ Using direct base64 student image");
    return { type: 'base64', data: cleanedPath };
  }

  // ✅ Raw base64 string
  if (this.isBase64String(cleanedPath)) {
    console.log("✅ Formatting raw base64 student image");
    const formattedImage = this.formatBase64ForPDF(cleanedPath);
    return { type: 'base64', data: formattedImage };
  }

  // ✅ Check cache
  const cacheKey = `student_${cleanedPath}`;
  const cachedImage = this.getCachedImage(cacheKey);
  if (cachedImage) {
    console.log("✅ Using cached student image");
    return { type: 'base64', data: cachedImage };
  }

  // ✅ Build full URL
  const baseUrl = process.env.REACT_APP_BASE_URL
    ? process.env.REACT_APP_BASE_URL.replace('/api', '').replace(/\/$/, '')
    : 'https://arizshad-002-site5.ktempurl.com';

  const fullUrl = cleanedPath.startsWith('http')
    ? cleanedPath
    : `${baseUrl}/${cleanedPath.replace(/^[~\/]+/, '')}`;

  console.log("Full student image URL:", fullUrl);

  // ✅ Strategy 1: HTML Image element + Canvas (bypasses CORS preflight — works on production)
  const tryImageElement = () => {
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';

      const timeout = setTimeout(() => {
        console.warn("⏰ Image element timeout");
        img.src = '';
        resolve(null);
      }, 8000);

      img.onload = () => {
        clearTimeout(timeout);
        try {
          const canvas = document.createElement('canvas');
          canvas.width = img.naturalWidth || 200;
          canvas.height = img.naturalHeight || 200;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0);
          const dataUrl = canvas.toDataURL('image/png');
          console.log("✅ Image loaded via HTML Image element + Canvas");
          resolve(dataUrl);
        } catch (canvasErr) {
          console.warn("⚠️ Canvas tainted (CORS blocked canvas):", canvasErr.message);
          resolve(null);
        }
      };

      img.onerror = () => {
        clearTimeout(timeout);
        console.warn("⚠️ HTML Image element failed");
        resolve(null);
      };

      img.src = fullUrl;
    });
  };

  // ✅ Strategy 2: fetch via CORS proxies
  const tryProxy = async (proxyUrl, proxyName) => {
    try {
      console.log(`🔄 Trying proxy: ${proxyName}`);
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);

      const response = await fetch(proxyUrl, {
        signal: controller.signal,
        headers: { 'Accept': 'image/*' },
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        console.warn(`⚠️ ${proxyName} returned ${response.status}`);
        return null;
      }

      const blob = await response.blob();
      if (!blob.type.startsWith('image/')) {
        console.warn(`⚠️ ${proxyName} non-image type: ${blob.type}`);
        return null;
      }

      const base64Data = await this.blobToBase64(blob);
      console.log(`✅ Image loaded via proxy: ${proxyName}`);
      return base64Data;

    } catch (e) {
      console.warn(`❌ Proxy ${proxyName} failed:`, e.message);
      return null;
    }
  };

  // --- Run strategies in order ---

  // Strategy 1: HTML Image + Canvas
  const imageElementResult = await tryImageElement();
  if (imageElementResult) {
    this.cacheImage(cacheKey, imageElementResult);
    return { type: 'base64', data: imageElementResult };
  }

  // Strategy 2: allorigins proxy
  const allOriginsResult = await tryProxy(
    `https://api.allorigins.win/raw?url=${encodeURIComponent(fullUrl)}`,
    'allorigins'
  );
  if (allOriginsResult) {
    this.cacheImage(cacheKey, allOriginsResult);
    return { type: 'base64', data: allOriginsResult };
  }

  // Strategy 3: corsproxy.io
  const corsProxyResult = await tryProxy(
    `https://corsproxy.io/?${encodeURIComponent(fullUrl)}`,
    'corsproxy.io'
  );
  if (corsProxyResult) {
    this.cacheImage(cacheKey, corsProxyResult);
    return { type: 'base64', data: corsProxyResult };
  }

  // Strategy 4: cors.sh
  const corsSHResult = await tryProxy(
    `https://cors.sh/${fullUrl}`,
    'cors.sh'
  );
  if (corsSHResult) {
    this.cacheImage(cacheKey, corsSHResult);
    return { type: 'base64', data: corsSHResult };
  }

  // All failed
  console.log("❌ All strategies failed, using default avatar");
  console.log("=== STUDENT IMAGE DEBUGGING END ===");
  return this.createDefaultAvatar();
}

  isBase64String(str) {
    const base64Patterns = [
      '/9j/',
      'iVBORw0KGgoAAAANSUhEUgAA',
      'R0lGODlh',
      'UklGR',
      'Qk0'
    ];
    return base64Patterns.some(pattern => str.startsWith(pattern));
  }

  getLogoSources(logoUrl) {
    console.log("Generating logo sources for:", logoUrl);

    const baseUrl = process.env.REACT_APP_BASE_URL
      ? process.env.REACT_APP_BASE_URL.replace('/api', '').replace(/\/$/, '')
      : 'https://arizshad-002-site5.ktempurl.com';

    let fullUrl;
    if (logoUrl.startsWith('http://') || logoUrl.startsWith('https://')) {
      fullUrl = logoUrl;
    } else {
      const cleanPath = logoUrl.replace(/^[~\/]+/, '');
      fullUrl = `${baseUrl}/${cleanPath}`;
    }

    return [
      { name: "direct", url: fullUrl, method: "direct" },
      {
        name: "cors-proxy-allorigins",
        url: `https://api.allorigins.win/raw?url=${encodeURIComponent(fullUrl)}`,
        method: "proxy"
      },
      {
        name: "cors-proxy-corsproxy",
        url: `https://corsproxy.io/?${encodeURIComponent(fullUrl)}`,
        method: "proxy"
      }
    ];
  }

  formatBase64ForPDF(base64String) {
    if (base64String.startsWith('data:image/')) {
      return base64String;
    }

    let mimeType = 'image/jpeg';
    if (base64String.startsWith('/9j/')) {
      mimeType = 'image/jpeg';
    } else if (base64String.startsWith('iVBORw0KGgoAAAANSUhEUgAA')) {
      mimeType = 'image/png';
    } else if (base64String.startsWith('R0lGODlh')) {
      mimeType = 'image/gif';
    } else if (base64String.startsWith('UklGR')) {
      mimeType = 'image/webp';
    }

    return `data:${mimeType};base64,${base64String}`;
  }

  async loadImageFromSource(source) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      console.log(`⏰ Timeout for ${source.name}`);
      controller.abort();
    }, 10000);

    try {
      console.log(`🔄 Fetching from ${source.name}: ${source.url}`);
      const response = await fetch(source.url, {
        signal: controller.signal,
        headers: {
          'Accept': 'image/*',
          'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36'
        },
        mode: 'cors'
      });

      clearTimeout(timeoutId);
      console.log(`📊 Response status for ${source.name}:`, response.status);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const blob = await response.blob();
      console.log(`📊 Blob info:`, { type: blob.type, size: blob.size });

      if (!blob.type.startsWith('image/')) {
        throw new Error(`Invalid content type: ${blob.type}`);
      }

      const base64Data = await this.blobToBase64(blob);
      console.log(`✅ Successfully converted to base64, length: ${base64Data.length}`);
      return base64Data;

    } catch (error) {
      clearTimeout(timeoutId);
      console.error(`❌ Error loading from ${source.name}:`, error);
      throw error;
    }
  }

  blobToBase64(blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = (error) => {
        console.error("FileReader error:", error);
        reject(error);
      };
      reader.readAsDataURL(blob);
    });
  }

  getCachedImage(imagePath) {
    return this.imageCache.get(imagePath);
  }

  cacheImage(imagePath, imageData) {
    if (this.imageCache.size >= this.maxCacheSize) {
      const firstKey = this.imageCache.keys().next().value;
      this.imageCache.delete(firstKey);
    }
    this.imageCache.set(imagePath, imageData);
  }

 createDefaultAvatar() {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  canvas.width = 100;
  canvas.height = 120;

  // Background - solid gray like Instagram
  ctx.fillStyle = '#a8a8a8';
  ctx.fillRect(0, 0, 100, 120);

  // Head - circle
  ctx.fillStyle = '#d4d4d4';
  ctx.beginPath();
  ctx.arc(50, 42, 22, 0, 2 * Math.PI);
  ctx.fill();

  // Body - circle segment clipped at bottom
  ctx.fillStyle = '#d4d4d4';
  ctx.beginPath();
  ctx.arc(50, 130, 42, 0, 2 * Math.PI);
  ctx.fill();

  return {
    type: 'canvas',
    data: canvas.toDataURL('image/png', 0.95)
  };
}

  addPlaceholderToPDF(doc, x, y, width, height) {
    doc.setFillColor(240, 240, 240);
    doc.rect(x, y, width, height, "F");
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.2);
    doc.rect(x, y, width, height);

    doc.setFontSize(6);
    doc.setTextColor(150, 150, 150);
    doc.text("Photo", x + width / 2, y + height / 2, { align: 'center' });
  }

  // ✅ UPDATED: Uses schoolDetails from API instead of sessionStorage
  addStudentContent(doc, studentData, schoolDetails) {
    // Student name
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0);
    doc.text(studentData.studentName || "N/A", 45, 58, { align: 'center' });

    // Details box
    doc.setFillColor(245, 245, 245);
    doc.rect(7, 62, 76, 40, "F");
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.2);
    doc.rect(7, 62, 76, 37.1);

    const details = [
      `Class: ${studentData.class || "N/A"}`,
      `Admission No: ${studentData.admissionNo || "N/A"}`,
      `Phone: ${studentData.fatherMobile || "N/A"}`,
      `Father Name: ${studentData.guardianName || "N/A"}`,
      `Address: ${studentData.permanentAddress || "N/A"}`,
    ];

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    let yPos = 67;
    details.forEach(detail => {
      const wrappedText = doc.splitTextToSize(detail, 70);
      wrappedText.forEach(line => {
        doc.text(line, 10, yPos);
        yPos += 5;
      });
    });

    // ✅ Footer — uses address and phoneNo from API
    doc.setFillColor(0, 51, 102);
    doc.rect(0, 100, 90, 15, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(255, 255, 255);

    const address = schoolDetails.address || '';
    const phoneNo = schoolDetails.phoneNo || '';

    if (phoneNo) {
      doc.text(`(Phone: ${phoneNo})`, 45, 113, { align: 'center' });
    }

    if (address) {
      const lines = doc.splitTextToSize(address, 80);
      const maxLines = 2;
      const displayLines = lines.slice(0, maxLines);
      displayLines.forEach((line, index) => {
        doc.text(line, 45, 105 + (index * 4), { align: 'center' });
      });
    }
  }

  generateAndOpenPDF(doc) {
    const pdfBlob = doc.output("blob");
    const pdfUrl = URL.createObjectURL(pdfBlob);
    window.open(pdfUrl, "_blank");
    setTimeout(() => URL.revokeObjectURL(pdfUrl), 5000);
  }

  showLoadingIndicator() {
    const loader = document.getElementById('pdf-loader') || this.createLoader();
    loader.style.display = 'block';
  }

  hideLoadingIndicator() {
    const loader = document.getElementById('pdf-loader');
    if (loader) loader.style.display = 'none';
  }

  createLoader() {
    const loader = document.createElement('div');
    loader.id = 'pdf-loader';
    loader.innerHTML = `
      <div style="position: fixed; top: 0; left: 0; width: 100%; height: 100%; 
                  background: rgba(0,0,0,0.5); display: flex; align-items: center; 
                  justify-content: center; z-index: 9999;">
        <div style="background: white; padding: 20px; border-radius: 8px; text-align: center;">
          <div style="width: 40px; height: 40px; border: 4px solid #f3f3f3; 
                      border-top: 4px solid #3498db; border-radius: 50%; 
                      animation: spin 1s linear infinite; margin: 0 auto 15px;"></div>
          <p>Generating ID Card...</p>
        </div>
      </div>
    `;

    const style = document.createElement('style');
    style.textContent = `
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `;
    document.head.appendChild(style);
    document.body.appendChild(loader);
    return loader;
  }

  showErrorMessage(message) {
    const errorDiv = document.createElement('div');
    errorDiv.innerHTML = `
      <div style="position: fixed; top: 20px; right: 20px; background: #f44336; 
                  color: white; padding: 16px; border-radius: 4px; z-index: 10000;
                  box-shadow: 0 4px 6px rgba(0,0,0,0.1); max-width: 400px;">
        <div style="font-weight: bold; margin-bottom: 8px;">PDF Generation Error</div>
        <div style="font-size: 14px;">${message}</div>
        <button onclick="this.parentElement.remove()" 
                style="position: absolute; top: 8px; right: 8px; 
                       background: none; border: none; color: white; 
                       cursor: pointer; font-size: 18px;">&times;</button>
      </div>
    `;
    document.body.appendChild(errorDiv);
    setTimeout(() => {
      if (errorDiv.parentElement) errorDiv.remove();
    }, 5000);
  }
}

 // Initialize PDF Manager
  const pdfManager = new ImprovedPDFImageManager();

const Admission = () => {
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
const [isUploading, setIsUploading] = useState(false);
  const [isPrintingAdmission, setIsPrintingAdmission] = useState(false);
  const [isMaster, setIsMaster] = useState(false);
const [branches, setBranches] = useState([]);
const [selectedBranchId, setSelectedBranchId] = useState(1);

  const [isPreloadingImages, setIsPreloadingImages] = useState(false);
  const [canAdd, setCanAdd] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [canPrint, setCanPrint] = useState(false);

  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [admissionData, setAdmissionData] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(30);
  const [open, setOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedAdmission, setSelectedAdmission] = useState(null);

  const decodeFormId = (encodedFormId) => {
    if (!encodedFormId || typeof encodedFormId !== "string") {
      console.error("encodedFormId is missing or invalid:", encodedFormId);
      return null;
    }
    try {
      const paddedEncodedFormId = encodedFormId.padEnd(Math.ceil(encodedFormId.length / 4) * 4, "=");
      const bytes = base64.toByteArray(paddedEncodedFormId);
      return new TextDecoder().decode(bytes);
    } catch (error) {
      console.error("Error decoding formId:", error);
      return null;
    }
  };

  const fetchBranches = async () => {
  try {
    const apiUrl = process.env.REACT_APP_BASE_URL;
    const token = sessionStorage.getItem("token");
    const response = await fetch(`${apiUrl}/Branch/GetBranch`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: token,
      },
      body: JSON.stringify({}),
    });

    if (response.ok) {
      const data = await response.json();
      setBranches(data);
    } else {
      console.error("Failed to fetch branches");
    }
  } catch (error) {
    console.error("Error fetching branches:", error);
  }
};


  const { encodedFormId } = useParams();
  const formId = decodeFormId(encodedFormId);

  const Authorizer = async () => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem("token");
      const response = await fetch(`${apiUrl}/CPanel/Module_Authorizer`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({ formId, }),
      });

      const result = await response.json();
      const data = result[0];

      setCanEdit(data?.uModify === 1);
      setCanAdd(data?.uWrite === 1);
      setCanPrint(data?.uPrint === 1);
      setCanDelete(data?.uDelete === 1);
    } catch (error) {
      console.error("Authorization check failed:", error);
    }
  };

  let navigate = useNavigate();

 

  const handleOpen = () => {
    setOpen(true);
  };

  const handleOpenModal = () => {
    setOpenModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
  }

  const handleClose = () => {
    setOpen(false);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const handleFileChange = (event) => {
    setSelectedFile(event.target.files[0]);
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleViewDocuments = (student) => {
    setSelectedStudent(student);
    setOpen(true);
  };

  const fetchStudentAdmission = async (studentId) => {
    try {
       setIsPrintingAdmission(true); 
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem('token');
      const response = await fetch(`${apiUrl}/Admission/rptAdmission`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify({
          studentId: studentId,
        }),
      });

      if (!response.ok) {
        throw new Error(`Error fetching admission data: ${response.status}`);
      }

    const data = await response.json();
const studentData = {
  ...data[0],
  studentImagePath: data[0].studentProfileLogo || data[0].studentImagePath
};
generateAdmissionFormPDFInNewTab(studentData);
    } catch (error) {
      console.error(error);
      setIsPrintingAdmission(false);
    }
  };

const generateAdmissionFormPDFInNewTab = async (studentData) => {
  try {
    const doc = new jsPDF();

    // Fetch school details
    let schoolName = "N/A";
    let schoolAddress = "N/A";
    let contactInfo = "00";
    let logoDataUrl = null;

    try {
      const token = sessionStorage.getItem("token");
      if (token) {
        const response = await fetch('https://wsms.aidigitalsec.com/api/FeeReport/GetSchoolName', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': token,
          },
          body: JSON.stringify({}),
        });

        if (response.ok) {
          const apiData = await response.json();
          schoolName = apiData[0]?.schoolName?.replace(/['"]+/g, "") || "N/A";
          schoolAddress = apiData[0]?.address?.replace(/['"]+/g, "") || "N/A";
          contactInfo = apiData[0]?.phoneNo?.replace(/['"]+/g, "") || "00";

          // Process logo
          const logoImg = apiData?.[0]?.logoImg;
          if (logoImg && typeof logoImg === 'string' && logoImg.trim() !== '') {
            try {
              let cleanedBase64 = logoImg
                .replace(/^data:image\/[a-zA-Z]+;base64,/, '')
                .replace(/\s/g, '')
                .replace(/\n/g, '');
              const paddingLength = (4 - (cleanedBase64.length % 4)) % 4;
              cleanedBase64 += '='.repeat(paddingLength);

              if (/^[A-Za-z0-9+/=]+$/.test(cleanedBase64)) {
                logoDataUrl = `data:image/png;base64,${cleanedBase64}`;
              }
            } catch (e) {
              console.warn("Logo processing failed:", e.message);
            }
          }
        }
      }
    } catch (error) {
      console.error("Error fetching school details:", error);
    }

let studentImageDataUrl = null;

// studentProfileLogo is already base64 in the API response
const rawBase64 = studentData.studentProfileLogo;

if (rawBase64 && rawBase64.trim() !== '') {
  try {
    // If it's already a data URL
    if (rawBase64.startsWith('data:image/')) {
      studentImageDataUrl = rawBase64;
    } else {
      // Raw base64 string — detect format and prefix it
      let mimeType = 'image/jpeg';
      if (rawBase64.startsWith('iVBORw0KGgo')) mimeType = 'image/png';
      else if (rawBase64.startsWith('R0lGODlh')) mimeType = 'image/gif';

      studentImageDataUrl = `data:${mimeType};base64,${rawBase64}`;
    }
    console.log("✅ Student image ready from base64");
  } catch (e) {
    console.warn("⚠️ Base64 processing error:", e.message);
  }
}

// Fallback
if (!studentImageDataUrl) {
  console.warn("⚠️ Using default avatar");
  studentImageDataUrl = pdfManager.createDefaultAvatar().data;
}

    // --- Page border ---
    doc.setDrawColor(0, 51, 102);
    doc.setLineWidth(0.5);
    doc.rect(5, 5, 200, 287);

    // --- Header section ---
    // Logo: top-left
    if (logoDataUrl) {
      try {
         doc.addImage(logoDataUrl, 'PNG', 12, 10, 37, 37);
      } catch (e) {
        console.warn("Could not add logo:", e.message);
      }
    }

    // Student photo: top-right, clearly within border
    doc.addImage(studentImageDataUrl, 'PNG', 162, 10, 30, 38);
    doc.setDrawColor(100, 100, 100);
    doc.setLineWidth(0.3);
    doc.rect(162, 10, 30, 38); // border around photo

    // School name (centered, avoiding logo & photo)
    doc.setFont("times", "bold");
    doc.setFontSize(18);
    doc.setTextColor(0, 51, 102);
    doc.text(schoolName, 105, 20, { align: "center" });

    doc.setFontSize(11);
    doc.setTextColor(100, 100, 100);
    doc.text("(RECOGNIZED)", 105, 28, { align: "center" });

    // Split address
    const words = schoolAddress.split(' ');
    const midPoint = Math.ceil(words.length / 2);
    let addressPart1, addressPart2;
    if (words.length > 1) {
      addressPart1 = words.slice(0, midPoint).join(' ');
      addressPart2 = words.slice(midPoint).join(' ');
    } else {
      const midChar = Math.ceil(schoolAddress.length / 2);
      addressPart1 = schoolAddress.slice(0, midChar);
      addressPart2 = schoolAddress.slice(midChar);
    }

    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    doc.setFont("times", "normal");
    doc.text(addressPart1, 105, 35, { align: "center" });
    doc.text(addressPart2, 105, 41, { align: "center" });
    doc.text(`Phone: ${contactInfo}`, 105, 47, { align: "center" });

    // Divider
    doc.setDrawColor(0, 51, 102);
    doc.setLineWidth(1);
    doc.line(20, 52, 190, 52);

    // Admission Form Title
    doc.setFont("times", "bold");
    doc.setFontSize(15);
    doc.setTextColor(0, 51, 102);
    doc.text("ADMISSION FORM", 105, 61, { align: "center" });

    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.3);
    doc.line(80, 63, 130, 63);

    // Reset
    doc.setFont("times", "normal");
    doc.setFontSize(11);
    doc.setTextColor(0, 0, 0);
    doc.setLineWidth(0.2);

    // Admission info row
    doc.text(`Admission No: ${studentData.admissionNo || '_______________'}`, 15, 71);
    doc.text(`Academic Session: ${studentData.session || '_______________'}`, 130, 71);
    doc.text("Reg. on: _______________", 15, 79);
    doc.setFont("times", "bold");
    doc.text("(PLEASE USE CAPITAL LETTERS)", 15, 87);
    doc.setFont("times", "normal");
    doc.text(`Class: ${studentData.class || '____________'}`, 130, 79);

    // Data fields
    const dataFields = [
      { label: "Student's Name", value: `${studentData.studentName}`, isBox: true },
      { label: "Aadhaar No", value: `${studentData.adharNo}`, isBox: false, digits: 12 },
      { label: "Date of Birth", value: `${studentData.dob}`, isBox: true, format: 'DD/MM/YYYY' },
      { label: "Religion", value: `${studentData.religion}`, isBox: true },
      { label: "Father's Name", value: `${studentData.guardianName || '_________'}`, isBox: true },
      { label: "Father's Contact No", value: `${studentData.fatherMobile}`, isBox: false, digits: 10 },
      { label: "Father's Aadhaar No", value: `${studentData.adharNo}`, isBox: false, digits: 12 },
      { label: "Mother's Name", value: `${studentData.mothersName}`, isBox: true },
      { label: "Mother's Contact No", value: `${studentData.motherMobile}`, isBox: false, digits: 10 },
      { label: "Mother's Aadhaar No", value: `${studentData.adharNo}`, isBox: false, digits: 12 },
      { label: "Guardian's Name", value: `${studentData.grandFather}`, isBox: true },
      { label: "Nationality", value: `${studentData.nationality}`, isBox: true },
      { label: "Category", value: `${studentData.casteName || '________'}`, isBox: true },
      { label: "Blood Group", value: `${studentData.bloodGroup}`, isBox: true },
      {
        label: "Last School Attended",
        value: `${studentData.lastSchoolAttended || '________________________________'}`,
        isBox: false,
        isLine: true
      },
      {
        label: "Permanent/Temporary Address",
        value: `${studentData.permanentAddress}`,
        isBoxWithLines: true
      },
    ];

    let yPos = 95;

    dataFields.forEach(({ label, value, isBox, digits, format, isLine, isBoxWithLines }) => {
      if (label === "Last School Attended") {
        yPos += 6;
      } else if (label.startsWith("Permanent")) {
        yPos += 4;
      }

      doc.setFont("times", "bold");
      doc.text(`${label}:`, 15, yPos);
      doc.setFont("times", "normal");

      if (digits && (label.includes("Contact No") || label.includes("Aadhaar No"))) {
        let xPos = 90;
        const number = value || "";
        for (let i = 0; i < digits; i++) {
          doc.setFillColor(240, 240, 240);
          doc.rect(xPos, yPos - 5, 5, 7, 'F');
          doc.setDrawColor(100, 100, 100);
          doc.rect(xPos, yPos - 5, 5, 7);
          const digit = number[i] || "";
          doc.text(digit, xPos + 1.5, yPos + 1);
          xPos += 6;
        }
      } else if (format === 'DD/MM/YYYY' && value) {
        const dobParts = value.split('/');
        const [day = '', month = '', year = ''] = dobParts;
        let xPos = 90;
        [day, month, year].forEach((part, index) => {
          for (let i = 0; i < part.length; i++) {
            doc.setFillColor(240, 240, 240);
            doc.rect(xPos, yPos - 5, 5, 7, 'F');
            doc.setDrawColor(100, 100, 100);
            doc.rect(xPos, yPos - 5, 5, 7);
            doc.text(part[i], xPos + 1.5, yPos + 1);
            xPos += 6;
          }
          if (index < 2) {
            doc.text('/', xPos - 2, yPos + 1);
            xPos += 2;
          }
        });
      } else if (isBox) {
        doc.setFillColor(245, 245, 245);
        doc.rect(90, yPos - 5, 85, 7, 'F');
        doc.setDrawColor(100, 100, 100);
        doc.rect(90, yPos - 5, 85, 7);
        doc.text(`${value || ''}`, 92, yPos);
      } else if (isBoxWithLines) {
        const boxWidth = 180;
        const boxHeight = 28;
        doc.setFillColor(245, 245, 245);
        doc.rect(15, yPos + 2, boxWidth, boxHeight, 'F');
        doc.setDrawColor(100, 100, 100);
        doc.rect(15, yPos + 2, boxWidth, boxHeight);
        const lines = doc.splitTextToSize(value || "", boxWidth - 10);
        lines.forEach((line, index) => {
          doc.text(line, 20, yPos + 8 + index * 6);
        });
        yPos += boxHeight;
      } else if (isLine) {
        doc.setDrawColor(0, 51, 102);
        doc.line(90, yPos, 175, yPos);
        doc.text(`${value || ''}`, 92, yPos - 2);
      } else {
        doc.text(`${value || '__________________'}`, 90, yPos);
      }

      yPos += 10;
    });

    // --- Page 2 ---
    doc.addPage();
    doc.setDrawColor(0, 51, 102);
    doc.setLineWidth(0.5);
    doc.rect(5, 5, 200, 287);

    let yPosAddress = 25;

    doc.setFont("times", "bold");
    doc.setFontSize(13);
    doc.setTextColor(0, 51, 102);
    doc.text("DECLARATION BY PARENTS/GUARDIAN:", 15, yPosAddress);

    let boxX = 12, boxY = yPosAddress + 5;
    let boxWidth = 186, boxHeight = 65;

    doc.setFillColor(245, 245, 245);
    doc.rect(boxX, boxY, boxWidth, boxHeight, 'F');
    doc.setDrawColor(100, 100, 100);
    doc.rect(boxX, boxY, boxWidth, boxHeight);

    const declarationText = [
      "All the information furnished below is correct and verifiable.",
      "I and my child have gone through all prescribed rules and regulations and shall abide by them.",
      "All the fees deposited by us shall not be claimed on any ground.",
      "I/we declare that in case of any mishap or expulsion, no suit or claim will be initiated by me/us.",
      "I/we shall abide by the decision of the principal under all circumstances."
    ];

    doc.setFont("times", "normal");
    doc.setTextColor(0, 0, 0);
    let yPosDeclaration = boxY + 10;
    declarationText.forEach((line) => {
      doc.text(line, boxX + 5, yPosDeclaration);
      yPosDeclaration += 10;
    });

    doc.setFont("times", "bold");
    doc.text("Father's Sign: _____________", 12, yPosDeclaration + 20);
    doc.text("Mother's Sign: _____________", 70, yPosDeclaration + 20);
    doc.text("Guardian's Sign: ______________", 130, yPosDeclaration + 20);

    let yPosDocuments = yPosDeclaration + 35;

    doc.setFont("times", "bold");
    doc.setFontSize(13);
    doc.setTextColor(0, 51, 102);
    doc.text("DOCUMENTS RECEIVED:", 15, yPosDocuments);
    yPosDocuments += 15;

    const documents = [
      "DOB Certificate / Affidavit",
      "Transfer Certificate (TC)",
      "Electricity Bill (within 3 months)",
      "Photocopy of Student's Aadhar Card",
      "Photocopy of Mother's Aadhar Card",
      "Photocopy of Father's Aadhar Card",
      "Caste Certificate",
      "Income Certificate"
    ];

    doc.setFont("times", "normal");
    doc.setTextColor(0, 0, 0);
    documents.forEach((docLine) => {
      doc.setFillColor(240, 240, 240);
      doc.rect(15, yPosDocuments - 5, 5, 5, 'F');
      doc.setDrawColor(100, 100, 100);
      doc.rect(15, yPosDocuments - 5, 5, 5);
      doc.text(docLine, 25, yPosDocuments);
      yPosDocuments += 10;
    });

    doc.setFont("times", "bold");
    doc.text("Admission Clerk: __________________", 110, yPosDocuments + 20);

    // Open PDF
    const pdfBlob = doc.output("blob");
    const pdfUrl = URL.createObjectURL(pdfBlob);
    window.open(pdfUrl, "_blank");
    setTimeout(() => URL.revokeObjectURL(pdfUrl), 5000);
    setIsPrintingAdmission(false); 

  } catch (error) {
    console.error("Error generating PDF:", error.message);
    setIsPrintingAdmission(false); 
  }
};


 

  // Updated fetchStudentId function with better error handling
  const fetchStudentId = async (studentId) => {
    try {
      console.log("🔄 Fetching student data for ID:", studentId);
      
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem('token');
      
      if (!token) {
        throw new Error("Authentication token not found");
      }

      const response = await fetch(`${apiUrl}/Admission/rptAdmission`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify({
          studentId: studentId,
        }),
      });

      if (!response.ok) {
        throw new Error(`API request failed: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      console.log("📊 API response for student data:", data);

      if (!data || !data[0]) {
        throw new Error("No student data returned from API");
      }

      // Map studentProfileLogo to studentImagePath for consistency
      const studentData = {
        ...data[0],
        studentImagePath: data[0].studentProfileLogo || data[0].studentImagePath
      };

      console.log("✅ Student data prepared, generating ID card...");
      await pdfManager.generateIdCardPDF(studentData);
      
    } catch (error) {
      console.error("❌ Error fetching student data:", error);
      pdfManager.showErrorMessage(`Failed to fetch student data: ${error.message}`);
    }
  };

  const preloadStudentImages = async (studentsArray) => {
    setIsPreloadingImages(true);
    try {
      const promises = studentsArray.map(async (student) => {
        const studentData = { ...student, studentImagePath: student.studentProfileLogo };
        return await pdfManager.handleStudentImage(studentData);
      });
      await Promise.all(promises);
    } finally {
      setIsPreloadingImages(false);
    }
  };

  const handleDelete = async () => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem('token');

      const response = await fetch(`${apiUrl}/Admission/Id?Id=${selectedAdmission.studentId}`, {
        method: 'DELETE',
        headers: {
          Authorization: token,
        },
      });
      if (response.ok) {
        setSnackbarMessage('Admission deleted successfully!');
        setOpenSnackbar(true);
        setSnackbarSeverity('success');
      }
      if (!response.ok) {
        throw new Error(`Error deleting admission: ${response.status}`);
      }

      fetchAdmission();
      setShowDeleteModal(false);
    } catch (error) {
      console.error(error);
    }
  };

 const handleUpload = async () => {
  if (!selectedFile) {
    setSnackbarMessage('Please select a file to upload.');
    setSnackbarSeverity('error');
    setOpenSnackbar(true);
    return;
  }

  setIsUploading(true);

  const apiUrl = process.env.REACT_APP_BASE_URL;
  const token = sessionStorage.getItem('token');
  const formData = new FormData();
  formData.append("file", selectedFile);

  try {
    const response = await fetch(`${apiUrl}/Admission/importAdmission`, {
      method: 'POST',
      headers: {
        Authorization: token,
      },
      body: formData,
    });

    const result = await response.json();

    if (!response.ok) {
      // Extract the error message from the API response
      const errorMessage =  result.error || result.msg || 'Failed to upload file.';

      // Set the error message and severity
      setSnackbarMessage(errorMessage);
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
      return; // Exit early to avoid further execution
    }

    // Success case
    setSnackbarMessage('Data imported successfully');
    setSnackbarSeverity('success');
    setOpenSnackbar(true);
    handleCloseModal();
  } catch (error) {
    // Handle network errors or other unexpected issues
    setSnackbarMessage(error.message || 'Failed to upload file.');
    setSnackbarSeverity('error');
    setOpenSnackbar(true);
  } finally {
    setIsUploading(false);
  }
};

  const handleClick = () => {
    navigate("/addadmission");
  };

  const fetchAdmission = async () => {
  try {
    const apiUrl = process.env.REACT_APP_BASE_URL;
    const token = sessionStorage.getItem("token");
    const branchId = isMaster ? selectedBranchId : sessionStorage.getItem("branchId");

    const response = await fetch(`${apiUrl}/Admission/GetAdmission?Branch_Id=${branchId}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: token,
      },
      body: JSON.stringify({
        Branch_Id: branchId,
      }),
    });

    if (!response.ok) {
      throw new Error(`Error fetching admission data: ${response.status}`);
    }

    const data = await response.json();
    if (data.data === null && data.msg === "Record Not Found") {
      throw new Error("Record Not Found");
    }

    setAdmissionData(data);
    return data;
  } catch (error) {
    console.error(error);
  }
};


useEffect(() => {
  const initialize = async () => {
    const isMasterUser = sessionStorage.getItem("is_Master") === "true";
    setIsMaster(isMasterUser);

    if (isMasterUser) {
      fetchBranches();
    } else {
      const branchId = sessionStorage.getItem("branchId");
      setSelectedBranchId(branchId);
    }

    const [admissionDataResult] = await Promise.all([
      fetchAdmission(),
      Authorizer()
    ]);

    if (admissionDataResult && admissionDataResult.length > 0) {
      await preloadStudentImages(admissionDataResult);
    }
  };
  initialize();
}, []);



  useEffect(() => {
  const isMasterUser = sessionStorage.getItem("is_Master") === "true";
  setIsMaster(isMasterUser);

  if (isMasterUser) {
    fetchBranches();
  } else {
    const branchId = sessionStorage.getItem("branchId");
    setSelectedBranchId(branchId);
  }

  Authorizer();
}, []);


useEffect(() => {
  if (selectedBranchId) {
    fetchAdmission();
  }
}, [selectedBranchId]);



  const filteredData = admissionData.filter((year) => {
    const studentNameMatch = year.studentName
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    const classNameMatch = year.className
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    return studentNameMatch || classNameMatch;
  });

  // Show all filtered results when searching, otherwise paginate
  const displayedData = searchTerm
    ? filteredData
    : filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleChangePage = (newPage) => setPage(newPage);

  const handleDownload = async () => {
    const apiUrl = process.env.REACT_APP_BASE_URL;
    const token = sessionStorage.getItem('token');
    const branchId = isMaster ? selectedBranchId : sessionStorage.getItem("branchId");

    try {
      const response = await fetch(`${apiUrl}/Admission/DownloadExcel_AdmissionReport?Branch_Id=${branchId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify({}),
      });

      if (!response.ok) {
        throw new Error(`Error downloading file: ${response.status}`);
      }

      // Get current date and time
      const now = new Date();
      const formattedDate = now.toISOString().split('T')[0];
      const formattedTime = now.toTimeString().split(' ')[0].replace(/:/g, '-');
      const filename = `AdmissionReport_${formattedDate}_${formattedTime}.csv`;

      // Convert response to blob
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
    }
  };

  // Function to handle opening of image or PDF in new tab
  const handleViewDocument = (path) => {
    window.open(`https://arizshad-002-site5.ktempurl.com/${path}`, "_blank");
  };

  const handleDownloadSample = async () => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const response = await fetch(`${apiUrl}/Admission/downloadFormatImportAdmission`);
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = 'Sample_Import_Admission.xlsx';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading the file:', error);
    }
  };


  return (
    <Container
  sx={{
    width: '100%',
    maxWidth: '100% !important',
    padding: 0,
  }}
>
      <AppBar
  position="static"
  sx={{
    backgroundColor: "#0B1F3D",
    boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
  }}
>
  <Toolbar sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 3 }}>
    
    {/* Left: Title */}
    <Typography
      variant="h4"
      sx={{
        fontWeight: 700,
        letterSpacing: "0.5px",
        color: "#ffffff",
      }}
    >
      Admission Report
    </Typography>

    {/* Right: Branch Dropdown */}
   {isMaster && (
  <Box sx={{ position: "relative" }}>
    <FormControl size="small" sx={{ width: 220 }}>
      <InputLabel
        sx={{
          color: "rgba(255,255,255,0.6)",
          fontSize: "0.75rem",
          fontWeight: 500,
          letterSpacing: "0.6px",
          textTransform: "uppercase",
          top: "-2px",
          "&.Mui-focused": { color: "#64B5F6" },
          "&.MuiFormLabel-filled": { color: "rgba(255,255,255,0.7)" },
        }}
      >
        Branch
      </InputLabel>
      <Select
        value={selectedBranchId || ""}
        onChange={(e) => setSelectedBranchId(e.target.value)}
        label="Branch"
        startAdornment={
          <InputAdornment position="start">
            <LocationOnIcon sx={{ fontSize: 16, color: "rgba(255,255,255,0.5)", ml: 0.5 }} />
          </InputAdornment>
        }
        sx={{
          color: "#ffffff",
          fontWeight: 500,
          fontSize: "0.875rem",
          letterSpacing: "0.3px",
          backgroundColor: "rgba(255,255,255,0.07)",
          backdropFilter: "blur(8px)",
          borderRadius: "8px",
          transition: "background-color 0.2s ease, box-shadow 0.2s ease",

          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "rgba(255,255,255,0.15)",
            borderWidth: "1px",
            transition: "border-color 0.2s ease",
          },
          "&:hover": {
            backgroundColor: "rgba(255,255,255,0.11)",
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "rgba(255,255,255,0.35)",
            },
          },
          "&.Mui-focused": {
            backgroundColor: "rgba(255,255,255,0.1)",
            boxShadow: "0 0 0 3px rgba(100,181,246,0.18)",
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "#64B5F6",
              borderWidth: "1.5px",
            },
          },
          "& .MuiSelect-icon": {
            color: "rgba(255,255,255,0.5)",
            transition: "color 0.2s ease",
          },
          "&.Mui-focused .MuiSelect-icon": {
            color: "#64B5F6",
          },
          "& .MuiSelect-select": {
            py: 1.1,
          },
        }}
        MenuProps={{
          PaperProps: {
            sx: {
              mt: 0.5,
              borderRadius: "10px",
              border: "1px solid rgba(11,31,61,0.12)",
              boxShadow: "0 8px 32px rgba(11,31,61,0.18), 0 2px 8px rgba(0,0,0,0.08)",
              overflow: "hidden",
              "& .MuiList-root": { py: 0.5 },
            },
          },
        }}
      >
        {branches.map((branch) => (
          <MenuItem
            key={branch.branchId}
            value={branch.branchId}
            sx={{
              fontSize: "0.875rem",
              fontWeight: 500,
              color: "#0B1F3D",
              px: 2,
              py: 1.1,
              mx: 0.5,
              borderRadius: "6px",
              transition: "background-color 0.15s ease",
              "&:hover": {
                backgroundColor: "rgba(11,31,61,0.07)",
              },
              "&.Mui-selected": {
                backgroundColor: "rgba(25,118,210,0.1)",
                color: "#1565C0",
                fontWeight: 600,
                "&:hover": {
                  backgroundColor: "rgba(25,118,210,0.15)",
                },
              },
            }}  
          >
            {branch.branchName}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  </Box>
)}
  </Toolbar>
</AppBar>
      <TextField
        fullWidth
        variant="outlined"
        margin="normal"
        placeholder="Search by student name or class"
        value={searchTerm}
        onChange={handleSearch}
      />

      <Button
        variant="outlined"
        sx={{
          mt: 2,
          mx: 1,
          backgroundColor: "#2196f3",
          color: "#ffffff",
          "&:hover": {
            backgroundColor: "#2196f3", // Set the same color as the background to remove hover effect
          },
        }}
        onClick={handleClick}
      >
        NEW ADMISSION
      </Button>
      <Button
        variant="outlined"
        sx={{
          mt: 2,
          mx: 1,
          backgroundColor: "#f50057",
          color: "#ffffff",
          "&:hover": {
            backgroundColor: "#f50057",
          },
        }}
        onClick={handleOpenModal} // Open the modal
      >
        IMPORT
      </Button>

      <Button
        variant="outlined"
        sx={{
          mt: 2,
          mx: 1,
          backgroundColor: "#3f51b5",
          color: "#ffffff",
          "&:hover": {
            backgroundColor: "#3f51b5",
          },
        }}
        onClick={handleDownload} // Call the handleDownload function
      >
        <CloudDownload sx={{ mx: 1 }} />
        DOWNLOAD
      </Button>
       <Tooltip title={!canAdd && "You are not Authorized to Print"} arrow><span>
     <Button
  variant="outlined"
  sx={{
    mt: 2,
    mx: 1,
    backgroundColor: canPrint ? "#4caf50" : "#cccccc", // Different background color when disabled
    color: canPrint ? "#ffffff" : "#666666", // Different text color when disabled
    borderColor: canPrint ? "#4caf50" : "#cccccc", // Different border color when disabled
    "&:hover": {
      backgroundColor: canPrint ? "#45a049" : "#cccccc", // Hover effect only when enabled
    },
    "&.Mui-disabled": {
      backgroundColor: "#cccccc", // Background color when disabled
      color: "#666666", // Text color when disabled
      borderColor: "#cccccc", // Border color when disabled
    },
  }}
  onClick={() => navigate("/bulk-id-card")}
  disabled={!canPrint}
>
  GENERATE BULK ID CARD
</Button></span></Tooltip>



    <TableContainer component={Paper} sx={{ mt: 3, minWidth: "100%" }}>
  <Table
    sx={{
      minWidth: "100%",
      border: '1px solid #ccc',
      '& .MuiTableCell-root': {
        borderRight: '1px solid #ccc',
        fontSize: '1rem',
      },
    }}
    aria-label="admission table"
  >
    <TableHead>
      <TableRow>
        <TableCell sx={{ borderBottom: '1px solid #ccc', fontWeight: 'bold', fontSize: '1.1rem', backgroundColor: '#f5f5f5' }}>
          Admission No
        </TableCell>
        <TableCell sx={{ borderBottom: '1px solid #ccc', fontWeight: 'bold', fontSize: '1.1rem', backgroundColor: '#f5f5f5' }}>
          Student Name
        </TableCell>
        <TableCell sx={{ borderBottom: '1px solid #ccc', fontWeight: 'bold', fontSize: '1.1rem', backgroundColor: '#f5f5f5' }}>
          Class
        </TableCell>
        <TableCell sx={{ borderBottom: '1px solid #ccc', fontWeight: 'bold', fontSize: '1.1rem', backgroundColor: '#f5f5f5' }}>
          Mobile No
        </TableCell>
        <TableCell sx={{ borderBottom: '1px solid #ccc', fontWeight: 'bold', fontSize: '1.1rem', backgroundColor: '#f5f5f5' , textAlign:'center'}}>
          Documents
        </TableCell>
        <TableCell sx={{ borderBottom: '1px solid #ccc', fontWeight: 'bold', fontSize: '1.1rem', backgroundColor: '#f5f5f5' , textAlign:'center'}}>
          Admission Form
        </TableCell>
        <TableCell sx={{ borderBottom: '1px solid #ccc', fontWeight: 'bold', fontSize: '1.1rem', backgroundColor: '#f5f5f5' , textAlign:'center'}}>
          Identity Card
        </TableCell>
        <TableCell sx={{ borderBottom: '1px solid #ccc', fontWeight: 'bold', fontSize: '1.1rem', backgroundColor: '#f5f5f5', textAlign: 'center' }}>
          Action
        </TableCell>
      </TableRow>
    </TableHead>
    <TableBody>
      {displayedData.map((year) => (
        <TableRow key={year.admissionNo} sx={{ borderTop: '1px solid #ccc' }}>
          <TableCell sx={{ fontSize: '1.1rem' }}>{year.admissionNo}</TableCell>
          <TableCell sx={{ fontSize: '1.1rem' }}>{year.studentName}</TableCell>
          <TableCell sx={{ fontSize: '1.1rem' }}>{year.className}</TableCell>
          <TableCell sx={{ fontSize: '1.1rem' }}>{year.mobileNo}</TableCell>
          <TableCell sx={{ fontSize: '1.1rem', textAlign:'center' }}>
            <Button
              variant="contained"
              onClick={() => handleViewDocuments(year)}
              sx={{ marginRight: 1 }}
            >
              View
            </Button>
          </TableCell>
          <TableCell sx={{ fontSize: '1.1rem' , textAlign:'center'}}>
             <Tooltip title={!canPrint && "You are not Authorized to Print"} arrow><span>
            <IconButton size="small" disabled={!canPrint}>
              <Print onClick={() => fetchStudentAdmission(year.studentId)} />
            </IconButton></span></Tooltip>
          </TableCell>
          <TableCell sx={{ fontSize: '1.1rem' , textAlign:'center'}}>
                         <Tooltip title={!canPrint && "You are not Authorized to Print"} arrow><span>

            <IconButton onClick={async () => await fetchStudentId(year.studentId)} disabled={!canPrint}>
              <IdCard />
            </IconButton></span></Tooltip>
          </TableCell>
          <TableCell sx={{ fontSize: '1.1rem', textAlign: 'center' }}>
             <Tooltip title={!canEdit && "You are not Authorized to Edit"} arrow><span>
           <Button
  variant="contained"
  color="warning"
  onClick={() => navigate(`/updateadmission/${year.studentId}?Branch_Id=${isMaster ? selectedBranchId : sessionStorage.getItem("branchId")}`)}
  sx={{ marginRight: 1 }}
  startIcon={<Edit />}
  disabled={!canEdit}
>
  Edit
</Button>
</span></Tooltip>
             <Tooltip title={!canDelete && "You are not Authorized to Delete"} arrow><span>

            <Button
              variant="contained"
              color="error"
              onClick={() => {
                setSelectedAdmission(year);
                setShowDeleteModal(true);
              }}
              sx={{ marginRight: 1 }}
              startIcon={<Delete />}
              disabled={!canDelete}
            >
              Delete
            </Button></span></Tooltip>
          </TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
</TableContainer>

      {!searchTerm && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            marginTop: "16px",
          }}
        >
          <IconButton
            onClick={() => handleChangePage(page - 1)}
            disabled={page === 0}
            sx={{ color: page === 0 ? "grey" : "#2196f3" }}
          >
            <NavigateBefore />
          </IconButton>
          <span style={{ margin: "0 8px", fontSize: "16px" }}>{page + 1}</span>
          <IconButton
            onClick={() => handleChangePage(page + 1)}
            disabled={page + 1 >= Math.ceil(filteredData.length / rowsPerPage)}
            sx={{
              color:
                page + 1 >= Math.ceil(filteredData.length / rowsPerPage)
                  ? "grey"
                  : "#2196f3",
            }}
          >
            <NavigateNext />
          </IconButton>
        </div>
      )}

      {/* Modal for viewing documents */}
     <Modal
  open={open}
  onClose={handleClose}
  aria-labelledby="documents-modal-title"
  aria-describedby="documents-modal-description"
>
  <Box
    sx={{
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: 500,
      bgcolor: 'background.paper',
      borderRadius: 2,
      boxShadow: 24,
      overflow: 'hidden',
    }}
  >
    {/* Header */}
    <Box sx={{ backgroundColor: '#0B1F40', padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <Typography id="documents-modal-title" variant="h6" sx={{ color: '#fff' }}>
        View Documents
      </Typography>
   <IconButton
  edge="end"
  color="inherit"
  onClick={handleClose}
  aria-label="close"
  sx={{
    color: '#fff', // This ensures the icon color is white
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)', // Optional: Add a slight background color on hover for better visibility
    },
  }}
>
  <CloseIcon />
</IconButton>

    </Box>
    {/* Content */}
    <Box sx={{ padding: '20px' }}>
      {selectedStudent && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          <Button
            variant="outlined"
            onClick={() => handleViewDocument(selectedStudent.aadharPathFront)}
            disabled={!selectedStudent.aadharPathFront}
          >
            Aadhar Front
          </Button>
          <Button
            variant="outlined"
            onClick={() => handleViewDocument(selectedStudent.adharPathBack)}
            disabled={!selectedStudent.adharPathBack}
          >
            Aadhar Back
          </Button>
          <Button
            variant="outlined"
            onClick={() => handleViewDocument(selectedStudent.birthDocumentPath)}
            disabled={!selectedStudent.birthDocumentPath}
          >
            Birth Certificate
          </Button>
          <Button
            variant="outlined"
            onClick={() => handleViewDocument(selectedStudent.transferDocumentPath)}
            disabled={!selectedStudent.transferDocumentPath}
          >
            Transfer Certificate
          </Button>
          <Button
            variant="outlined"
            onClick={() => handleViewDocument(selectedStudent.otherDocumentPath)}
            disabled={!selectedStudent.otherDocumentPath}
          >
            Others
          </Button>
        </Box>
      )}
    </Box>
  </Box>
</Modal>


      {/* Modal for file upload */}
   <Modal
  open={openModal}
  onClose={!isUploading ? handleCloseModal : undefined}
  aria-labelledby="upload-modal-title"
  aria-describedby="upload-modal-description"
  closeAfterTransition
  BackdropComponent={Backdrop}
  BackdropProps={{ timeout: 500 }}
>
  <Fade in={openModal}>
    <Box
      sx={{
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        width: "60%",
        bgcolor: "background.paper",
        borderRadius: 1,
        boxShadow: 24,
        p: 4,
      }}
    >
      <AppBar
        position="static"
        style={{ backgroundColor: "#0B1F3D", marginBottom: "20px" }}
      >
        <Toolbar sx={{ display: "flex", justifyContent: "space-between" }}>
          <Typography variant="h4" component="div">
            Upload Excel File
          </Typography>
          <IconButton
            edge="end"
            color="inherit"
            onClick={handleCloseModal}
            disabled={isUploading}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Typography
        id="upload-modal-title"
        variant="h6"
        component="div"
        gutterBottom
      >
        Tips for Uploading File
      </Typography>
      <ul
        style={{
          marginBottom: "20px",
          paddingLeft: "20px",
          listStyleType: "disc",
        }}
      >
        <li>Ensure the file is in .xlsx format.</li>
        <li>Make sure all required fields are filled out.</li>
        <li>Check for any duplicate entries.</li>
        <li>Remove any special characters from the data.</li>
        <li>
          Ensure proper column headers are used as per the template.
        </li>
      </ul>

      <input
        type="file"
        onChange={handleFileChange}
        disabled={isUploading}
        style={{ marginBottom: "20px", display: "block" }}
      />

      <Box display="flex" justifyContent="space-between">
        <Button
          variant="contained"
          onClick={handleUpload}
          color="primary"
          disabled={!selectedFile || isUploading}
          startIcon={
            isUploading ? (
              <CircularProgress size={16} color="inherit" />
            ) : null
          }
        >
          {isUploading ? "Uploading..." : "Upload"}
        </Button>
        <Button
          variant="contained"
          onClick={handleDownloadSample}
          color="warning"
          disabled={isUploading}
        >
          Download Sample Report
        </Button>
      </Box>
    </Box>
  </Fade>
</Modal>

      {/* Delete Confirmation Modal */}
      <Modal open={showDeleteModal} onClose={handleCloseDeleteModal} aria-labelledby="delete-modal-title" aria-describedby="delete-modal-description">
  <Box
    sx={{
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: 400,
      bgcolor: 'background.paper',
      borderRadius: 2,
      boxShadow: 24,
      overflow: 'hidden',
    }}
  >
    {/* Header */}
    <Box sx={{ backgroundColor: '#0B1F40', padding: '12px 16px' }}>
      <Typography id="delete-modal-title" variant="h6" sx={{ color: '#fff' }}>
        Delete Confirmation ⚠️
      </Typography>
    </Box>

    {/* Content */}
    <Box sx={{ padding: '20px' }}>
      <Typography id="delete-modal-description" sx={{ mb: 2 }}>
        Are you sure you want to delete this Admission?
      </Typography>

      {/* Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
        <Button onClick={handleCloseDeleteModal} variant="outlined" sx={{ mr: 1 }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleDelete}
        >
          Yes, Delete
        </Button>
      </Box>
    </Box>
  </Box>
</Modal>

<Snackbar
  open={openSnackbar}
  autoHideDuration={4000}
  onClose={() => setOpenSnackbar(false)}
  anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
>
  <Alert
    onClose={() => setOpenSnackbar(false)}
    severity={snackbarSeverity}
    sx={{ width: '100%' }}
  >
    {snackbarMessage}
  </Alert>
</Snackbar>

{isPrintingAdmission && (
  <Box
    sx={{
      position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
      background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center',
      justifyContent: 'center', zIndex: 9999,
    }}
  >
    <Box sx={{ background: 'white', padding: '20px', borderRadius: '8px', textAlign: 'center' }}>
      <div style={{
        width: '40px', height: '40px', border: '4px solid #f3f3f3',
        borderTop: '4px solid #3498db', borderRadius: '50%',
        animation: 'spin 1s linear infinite', margin: '0 auto 15px',
      }} />
      <Typography>Generating Admission Form...</Typography>
    </Box>
  </Box>
)}
    </Container>
  );
};

export default Admission;